import type { OperationVariables } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react/compiled'
import { recoverWsAuth } from '@dolpheen/apollo/wsAuth'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { useSubscription } from '@hooks/graphql'
import { extractGraphqlErrors, shouldRefreshJwtToken } from '@sections/auth/errors'
import { isMessagingSimulationEnabled } from '@sections/messaging/constants'
import { useEffect, useRef } from 'react'

// On a subscription error graphql-ws COMPLETES the operation: Apollo will not
// re-execute it on its own, so a single failed (re-)subscribe — e.g. a token
// that was momentarily expired when the socket reconnected after laptop wake —
// silently kills live delivery for the rest of the session. We restart it
// ourselves with a capped backoff so one transient failure isn't permanent.
const RESTART_BASE_MS = 2_000
const RESTART_MAX_MS = 30_000

interface AutoRestartOptions<TData, TVariables extends OperationVariables, TEvent> {
  variables: TVariables
  /** Caller-side skip (e.g. no open thread); dev simulation is always skipped. */
  skip?: boolean
  /** Used only in the restart-on-error log line. */
  label: string
  /** Pull the event off the subscription payload. */
  select: (data: TData | undefined) => TEvent | null | undefined
  /** Runs only when the event names a conversation (payload-less frames ignored). */
  onEvent: (event: TEvent) => void
}

/**
 * Shared machinery for the messaging live subscriptions (open-thread +
 * inbox-list): a content-keyed subscription that auto-restarts on a transient
 * error with capped backoff, resets the backoff once a live frame proves health,
 * and resets the backoff + cancels any pending restart when the subscription
 * identity changes (variables or skip) — so a stale restart scheduled for a
 * previous conversation/scope never fires against the new one. Dev simulation
 * skips the live socket entirely.
 */
export function useAutoRestartSubscription<
  TData,
  TVariables extends OperationVariables,
  TEvent extends { conversationId?: string | null },
>(
  document: TypedDocumentNode<TData, TVariables>,
  { label, onEvent, select, skip, variables }: AutoRestartOptions<TData, TVariables, TEvent>,
): void {
  const client = useApolloClient()
  const restartTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const attemptsRef = useRef(0)

  const clearRestart = () => {
    if (restartTimer.current) {
      clearTimeout(restartTimer.current)
      restartTimer.current = undefined
    }
  }

  const { restart } = useSubscription(document, {
    onData: (update) => {
      // A live frame proves the subscription is healthy again — clear any
      // pending restart and reset the backoff.
      attemptsRef.current = 0
      clearRestart()
      const event = select(update.data.data ?? undefined)
      if (event?.conversationId) {
        onEvent(event)
      }
    },
    onError: (error) => {
      const attempt = attemptsRef.current++
      const delay = Math.min(RESTART_BASE_MS * 2 ** attempt, RESTART_MAX_MS)
      console.error(
        `${label} subscription errored (attempt ${attempt + 1}); restarting in ${delay}ms`,
        error,
      )
      clearRestart()

      // An expired JWT can never heal by retrying: the ws connection's auth is
      // frozen at connect time. Terminate the socket first, THEN restart —
      // the reconnect re-runs `connectionParams`, which refreshes the token
      // (see client.ts / wsAuth.ts). The restart delay covers the reconnect
      // (a hard auth failure surfaces through the HTTP layer's authErrorLink
      // as a logout; here it just falls back to the plain backoff).
      if (shouldRefreshJwtToken(extractGraphqlErrors(error))) {
        recoverWsAuth(client)
      }

      restartTimer.current = setTimeout(() => restart(), delay)
    },
    skip: isMessagingSimulationEnabled || skip,
    variables,
  })

  // A new subscription identity (variables/skip changed ⇒ Apollo re-subscribes)
  // resets the backoff and cancels a restart left pending from the previous
  // identity, which would otherwise fire restart() against the new variables.
  const identity = `${JSON.stringify(variables)}|${String(skip)}`
  useEffect(() => {
    attemptsRef.current = 0
    clearRestart()
  }, [identity])

  useEffect(() => () => clearRestart(), [])
}
